import { clearAllAuthAndWalletSessions, isSessionVerificationError } from "@/lib/session-cleanup";

const WALLET_ERROR_EVENT = "robotdex:wallet-error";
const LOG_KEY = "robotdex.wallet.errorLog";
const LAST_KEY = "robotdex.wallet.lastError";
const MAX_LOG_ENTRIES = 20;

export type WalletDiagnostic = {
  message: string;
  name?: string;
  stack?: string;
  at: string;
  url: string;
  userAgent: string;
  walletName?: string;
  context?: string;
  code?: string | number;
};

export type WalletErrorContext = {
  walletName?: string;
  context?: string;
};

function errorToDiagnostic(error: unknown, ctx?: WalletErrorContext): WalletDiagnostic {
  const err = error as { message?: string; stack?: string; code?: string | number; name?: string };
  return {
    message: err?.message || String(error),
    name: err?.name,
    stack: err?.stack,
    code: err?.code,
    at: new Date().toISOString(),
    url: typeof window === "undefined" ? "" : window.location.href,
    userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent,
    walletName: ctx?.walletName,
    context: ctx?.context,
  };
}

function pushToLog(diagnostic: WalletDiagnostic) {
  try {
    const raw = sessionStorage.getItem(LOG_KEY);
    const list: WalletDiagnostic[] = raw ? (JSON.parse(raw) as WalletDiagnostic[]) : [];
    list.unshift(diagnostic);
    sessionStorage.setItem(LOG_KEY, JSON.stringify(list.slice(0, MAX_LOG_ENTRIES)));
  } catch {
    // ignore storage failures
  }
}

export function reportWalletError(error: unknown, ctx?: WalletErrorContext) {
  if (typeof window === "undefined") return;
  const diagnostic = errorToDiagnostic(error, ctx);
  // Always mirror to the browser console for developer visibility.
  const label = diagnostic.walletName
    ? `[wallet:${diagnostic.walletName}]`
    : "[wallet]";
  // eslint-disable-next-line no-console
  console.error(label, diagnostic.context ?? "error", diagnostic.message, error);
  try {
    sessionStorage.setItem(LAST_KEY, JSON.stringify(diagnostic));
  } catch {
    // ignore storage failures
  }
  pushToLog(diagnostic);
  window.dispatchEvent(new CustomEvent<WalletDiagnostic>(WALLET_ERROR_EVENT, { detail: diagnostic }));
  if (isSessionVerificationError(error)) {
    void clearAllAuthAndWalletSessions();
  }
}

export function getLastWalletError(): WalletDiagnostic | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(LAST_KEY);
    return raw ? (JSON.parse(raw) as WalletDiagnostic) : null;
  } catch {
    return null;
  }
}

export function getWalletErrorLog(): WalletDiagnostic[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(LOG_KEY);
    return raw ? (JSON.parse(raw) as WalletDiagnostic[]) : [];
  } catch {
    return [];
  }
}

export function clearWalletErrors() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(LAST_KEY);
    sessionStorage.removeItem(LOG_KEY);
  } catch {
    // ignore
  }
}

export function formatWalletDiagnosticsForClipboard(entries = getWalletErrorLog()): string {
  return JSON.stringify(
    {
      latest: entries[0] ?? getLastWalletError(),
      entries,
      capturedAt: new Date().toISOString(),
    },
    null,
    2,
  );
}

export function subscribeWalletErrors(callback: (diagnostic: WalletDiagnostic) => void) {
  if (typeof window === "undefined") return () => undefined;
  const listener = (event: Event) => callback((event as CustomEvent<WalletDiagnostic>).detail);
  window.addEventListener(WALLET_ERROR_EVENT, listener);
  return () => window.removeEventListener(WALLET_ERROR_EVENT, listener);
}

// Turn a raw wallet/connector error into a short human-friendly message.
export function friendlyWalletMessage(error: unknown, walletName?: string): string {
  const err = error as { code?: string | number; message?: string; name?: string };
  const msg = err?.message ?? String(error);
  const wallet = walletName ?? "wallet";
  if (err?.code === 4001 || /reject|denied|user closed|user cancel/i.test(msg)) {
    return `Connection to ${wallet} was cancelled.`;
  }
  if (/not installed|no provider|provider is undefined|Cannot read prop/i.test(msg)) {
    return `${wallet} isn't available in this browser. Install the extension or open the site from the wallet's in-app browser.`;
  }
  if (/timeout|timed out/i.test(msg)) {
    return `Connecting to ${wallet} timed out. Please try again.`;
  }
  if (/network|chain|unsupported/i.test(msg)) {
    return `${wallet} reported a network problem. Please try again.`;
  }
  if (isSessionVerificationError(error)) {
    return `${wallet} could not finish wallet verification after returning to RobotDex.`;
  }
  return `Could not connect to ${wallet}. Please try again.`;
}
